import React, { useEffect, useState } from 'react';
import { useTimer } from '../context/TimerContext';
import { PlayIcon, StopIcon, ClockIcon, PauseIcon } from '@heroicons/react/24/solid';
import TimerClock from './TimerClock';

/**
 * TimerButton component - displays a button in the navbar to control timer
 * Shows current timer status and elapsed time when timer is running
 */
const TimerButton = ({ compact = false }) => {
  const { 
    currentTimer, 
    formattedTime, 
    elapsedSeconds,
    isLoading, 
    startTimer, 
    toggleTimerPanel,
    refreshCurrentTimer
  } = useTimer();
  
  // Local state for display time (for debugging)
  const [displayTime, setDisplayTime] = useState(formattedTime);
  
  // Update display time whenever formattedTime changes
  useEffect(() => {
    setDisplayTime(formattedTime);
    console.log("Timer updated:", formattedTime, "Elapsed seconds:", elapsedSeconds);
  }, [formattedTime, elapsedSeconds]);
  
  // Memoize to avoid unnecessary re-renders
  const isRunning = React.useMemo(() => !!currentTimer, [currentTimer]);

  const handleClick = async () => {
    if (isRunning) {
      // If timer is running, toggle the timer panel and refresh
      toggleTimerPanel();
      refreshCurrentTimer();
    } else {
      // Start a new timer immediately
      try {
        await startTimer();
      } catch (error) {
        console.error('Failed to start timer:', error);
      }
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
        isRunning
          ? currentTimer?.status === 'paused'
            ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
            : 'bg-grayteck-500 hover:bg-grayteck-600 text-white'
          : 'bg-grayteck-200 hover:bg-grayteck-300 dark:bg-darkbg-700 dark:hover:bg-darkbg-600 text-grayteck-800 dark:text-grayteck-200'
      }`}
    >
      {isLoading ? (
        <div className="w-4 h-4 border-2 border-grayteck-300 border-t-grayteck-600 rounded-full animate-spin" />
      ) : isRunning ? (
        <>
          <ClockIcon className="w-4 h-4" />
          <TimerClock timeEntryId={currentTimer?._id || currentTimer?.id} />
          {currentTimer?.status === 'paused' ? (
            <PauseIcon className="w-4 h-4 text-yellow-100" />
          ) : (
            <StopIcon className="w-4 h-4" />
          )}
        </>
      ) : (
        <>
          <PlayIcon className="w-4 h-4" />
          {!compact && <span>Start Timer</span>}
        </>
      )}
    </button>
  );
};

export default TimerButton;